import { Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { InspirationRemainingTotal } from "web_myplann/Inspiration/Components/InspirationRemainingTotal";
import { InspirationStageList } from "web_myplann/Inspiration/Components/InspirationStageList";
import { InspirationValueRow } from "web_myplann/Inspiration/Components/InspirationValueRow";
import type { InspirationStage } from "web_myplann/Inspiration/Types/InspirationTypes";

type Props = {
	rows: { label: string; value: string; valueColor?: string }[];
	remaining?: { label: string; value: string; suffix: string };
	stages?: InspirationStage[];
	capital?: { label: string; value: string; note: string };
};

export const InspirationCapitalCard: FC<Props> = ({
	rows,
	remaining,
	stages,
	capital,
}) => {
	const theme = useTheme();

	const divider = theme.colors.border.solid(theme.palette.grey[200]);

	return (
		<Stack
			bgcolor={theme.palette.surface.muted}
			borderRadius={theme.radii.lg}
			px={theme.spacingTokens.lg}
			py={theme.spacingTokens.md}
		>
			<Stack gap={theme.spacingTokens.xsm}>
				{rows.map((row) => (
					<InspirationValueRow
						key={row.label}
						label={row.label}
						value={row.value}
						valueColor={row.valueColor}
					/>
				))}
			</Stack>

			{remaining && (
				<InspirationRemainingTotal
					mt={theme.spacingTokens.sm}
					pt={theme.spacingTokens.sm}
					borderTop={divider}
					label={remaining.label}
					value={remaining.value}
					suffix={remaining.suffix}
				/>
			)}

			{stages && (
				<InspirationStageList
					stages={stages}
					mt={theme.spacingTokens.sm}
					pt={theme.spacingTokens.sm}
					borderTop={divider}
				/>
			)}

			{capital && (
				<Stack
					mt={theme.spacingTokens.lg}
					pt={theme.spacingTokens.lg}
					borderTop={divider}
				>
					<Typography variant="button" color={theme.palette.grey[500]}>
						{capital.label}
					</Typography>
					<Typography
						variant="h1"
						color={theme.palette.orange.main}
						tabular
						mt={theme.spacingTokens.xs}
					>
						~{capital.value}
					</Typography>
					<Typography
						variant="body1"
						color={theme.palette.grey[500]}
						mt={theme.spacingTokens.xxs}
					>
						{capital.note}
					</Typography>
				</Stack>
			)}
		</Stack>
	);
};
